import { ref } from 'vue';
import { z } from 'zod';
import { useInternational } from '@/common/locale';
import { useMessage } from '@/common/inject/useMessage';
import { ROLE_PERMISSION_KEY } from '@/common/define/rolePermission.define';
import { getSlideDetailStore, TOTAL_DETAIL } from '@/common/stores/slide-detail';
import { useTargetPermissionCheck } from '@/common/permission/permission.utils';
import { TargetStatusRequestTargetKind } from '@/api-client/target/model';
import type { RolePermissionKeyType } from '@/config/views/permission/rolePermission/rolePermission.type';
import type {
  SlideDetailAdapterParams,
  SlideDetailAdapterResult,
  SlideDetailAdapterResultKey,
} from '@/common/types/slideDetailAdapter.types';
import { createSlideDetailResult } from '@/common/utils/slideDetailAdapterResult';
import type { PanelInfo } from '@/common/stores/slide-detail.logic';

type WebApplicationBaseInput = PanelInfo<typeof TOTAL_DETAIL.WEB_APPLICATION>['application'];
export type WebApplicationAdapterInput = WebApplicationBaseInput & {
  targetId?: string;
  targetName?: string;
};

type NormalizedParams = Partial<WebApplicationAdapterInput> &
  Pick<WebApplicationBaseInput, 'applicationId'>;

const normalizedParamsSchema = z.custom<Partial<WebApplicationAdapterInput>>().transform(
  (input): NormalizedParams => ({
    ...input,
    applicationId: input.applicationId || input.targetId || '',
    name: input.name || input.targetName || '',
  }),
);

const webApplicationParamsSchema = z.object({
  applicationId: z.string().min(1),
  name: z.string().min(1),
  fromTime: z.string().optional(),
  toTime: z.string().optional(),
  fromMainView: z.boolean().optional(),
});

const getMissingParams = (error: z.ZodError) =>
  error.issues
    .map((issue) => issue.path[0])
    .filter((path): path is string => typeof path === 'string')
    .join(', ');

export const useWebApplicationAdapter = (rolePermissionKey?: RolePermissionKeyType) => {
  const isSlideDetailOpened = ref(false);
  const { showErrorMsg } = useMessage();
  const { t } = useInternational();
  const { addTabPanel, isDetailSlidePermissionDenied } = getSlideDetailStore();
  const { checkIsValidTarget } = useTargetPermissionCheck();

  const validateWebApplicationDetail = async (
    params: Partial<WebApplicationAdapterInput>,
    options: {
      showErrorMessage: boolean;
      checkPermission?: boolean;
      checkParams?: boolean;
      checkTarget?: boolean;
    },
  ): Promise<{
    reason: SlideDetailAdapterResultKey;
    parsedParams?: z.infer<typeof webApplicationParamsSchema>;
  }> => {
    const { checkPermission = true, checkParams = true, checkTarget = true } = options;
    const normalizedParams = normalizedParamsSchema.parse(params);

    // 1. 권한 체크
    if (checkPermission) {
      const denied = isDetailSlidePermissionDenied({
        type: TOTAL_DETAIL.WEB_APPLICATION,
        rolePermissionKey:
          rolePermissionKey ||
          ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION_WEB_APPLICATION,
        showError: options.showErrorMessage,
      });
      if (denied) return { reason: 'permission_denied' };
    }

    // 2. 타겟 유효성 검증
    if (checkTarget) {
      const targetValidation = await checkIsValidTarget(
        {
          targetId: normalizedParams.applicationId,
          targetKind: TargetStatusRequestTargetKind.RUM,
        },
        {
          showMessageIfTargetNil: options.showErrorMessage,
          showErrorMessage: options.showErrorMessage,
        },
      );
      if (!targetValidation.valid) return { reason: targetValidation.reason };
    }

    // 3. 대상 정보 조회 및 필수값 검증
    if (checkParams) {
      const parsed = webApplicationParamsSchema.safeParse(normalizedParams);
      if (!parsed.success) {
        const errorMsg = t('MESSAGE.LINK_MISSING_REQUIRED_PARAMS', {
          value: getMissingParams(parsed.error),
        });
        if (options.showErrorMessage) showErrorMsg({ msgStr: errorMsg });
        return { reason: 'invalid_params' };
      }
      return { reason: 'ok', parsedParams: parsed.data };
    }

    return { reason: 'ok' };
  };

  const openWebApplicationDetail = async ({
    params,
    options,
    selectedTab,
    init,
    state,
    currentState,
  }: SlideDetailAdapterParams<
    Partial<WebApplicationAdapterInput>
  >): Promise<SlideDetailAdapterResult> => {
    const {
      runCheckPermission = true,
      showErrorMessage = true,
      runTargetValidation = true,
    } = options ?? {};

    const { reason, parsedParams } = await validateWebApplicationDetail(params, {
      showErrorMessage,
      checkPermission: runCheckPermission,
      checkTarget: runTargetValidation,
    });
    if (reason !== 'ok' || !parsedParams) return createSlideDetailResult(reason);
    const validResult = parsedParams;

    // 4. 탭 패널 열기
    const result = addTabPanel(
      {
        type: TOTAL_DETAIL.WEB_APPLICATION,
        name: validResult.name,
        application: {
          applicationId: validResult.applicationId,
          name: validResult.name,
          fromTime: validResult.fromTime,
          toTime: validResult.toTime,
          fromMainView: validResult.fromMainView,
        },
        init,
        state: { ...state, selectedTab },
      },
      currentState,
    );

    isSlideDetailOpened.value = getSlideDetailStore().isOpened;
    return createSlideDetailResult(result);
  };

  return { isSlideDetailOpened, openWebApplicationDetail };
};
